import { useEffect, useRef, useState } from "react";
import type { CmmdTaskWorkflow } from "@cmmd-forge/contracts";
import { Settings2Icon } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from "~/components/ui/sheet";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import { ProjectTaskWorkflowEditor } from "./ProjectTaskWorkflowEditor";

const api = createCmmdApiClient();
export interface ProjectTaskWorkflowSettingsProps {
  readonly projectId: number;
  readonly className?: string;
  readonly onSaved?: (() => void | Promise<void>) | undefined;
}

export function ProjectTaskWorkflowSettings(props: ProjectTaskWorkflowSettingsProps) {
  const [open, setOpen] = useState(false);
  const lifecycleGeneration = useRef(0);
  const [loadGeneration, setLoadGeneration] = useState(0);
  const [state, setState] = useState<
    | { readonly kind: "idle" | "loading" }
    | {
        readonly kind: "ready";
        readonly draft: CmmdTaskWorkflow;
        readonly saving: boolean;
        readonly error: string | null;
      }
    | { readonly kind: "error"; readonly message: string }
  >({ kind: "idle" });

  useEffect(() => {
    if (!open) return;
    let active = true;
    setState({ kind: "loading" });
    void api.getProjectTaskWorkflow(String(props.projectId)).then(
      (draft) => {
        if (active) setState({ kind: "ready", draft, saving: false, error: null });
      },
      () => {
        if (active) setState({ kind: "error", message: "We couldn’t load this project workflow." });
      },
    );
    return () => {
      active = false;
    };
  }, [open, props.projectId, loadGeneration]);

  const setDraft = (draft: CmmdTaskWorkflow) => {
    setState({ kind: "ready", draft, saving: false, error: null });
  };

  const save = async () => {
    if (state.kind !== "ready") return;
    if (state.draft.stages.some((stage) => stage.name.trim().length === 0)) {
      setState({ ...state, error: "Every workflow stage needs a name." });
      return;
    }
    const saveGeneration = lifecycleGeneration.current;
    setState({ ...state, saving: true, error: null });
    try {
      const draft = await api.updateProjectTaskWorkflow(String(props.projectId), state.draft);
      if (lifecycleGeneration.current !== saveGeneration) return;
      setState({ kind: "ready", draft, saving: false, error: null });
      await props.onSaved?.();
    } catch {
      if (lifecycleGeneration.current !== saveGeneration) return;
      setState({
        ...state,
        saving: false,
        error: "We couldn’t save this workflow. Review the stages and try again.",
      });
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label="Task workflow settings"
        title="Task workflow settings"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          RESPONSIVE_ICON_BUTTON_CLASS,
          props.className,
        )}
      >
        <Settings2Icon className="size-4" aria-hidden />
      </button>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          lifecycleGeneration.current += 1;
          setOpen(nextOpen);
          if (!nextOpen) setState({ kind: "idle" });
        }}
      >
        <SheetPopup side="right" variant="inset" className="sm:max-w-[640px]">
          <SheetHeader className="border-b border-border pr-14">
            <SheetTitle>Task workflow</SheetTitle>
            <SheetDescription>
              Workspace stages control this board and every other project in the workspace.
            </SheetDescription>
          </SheetHeader>
          <SheetPanel className="space-y-8">
            {state.kind === "loading" || state.kind === "idle" ? (
              <p className="text-sm text-muted-foreground">Loading workflow…</p>
            ) : null}
            {state.kind === "error" ? (
              <div className="rounded-lg border border-border p-4 text-sm">
                <p>{state.message}</p>
                <Button
                  className="mt-3"
                  onClick={() => setLoadGeneration((generation) => generation + 1)}
                  size="sm"
                  variant="outline"
                >
                  Try again
                </Button>
              </div>
            ) : null}
            {state.kind === "ready" ? (
              <ProjectTaskWorkflowEditor
                draft={state.draft}
                disabled={state.saving}
                onChange={setDraft}
              />
            ) : null}
          </SheetPanel>
          {state.kind === "ready" ? (
            <SheetFooter className="items-center justify-between sm:justify-between">
              <p className="text-xs text-muted-foreground">
                {state.error ??
                  "CMMD enforces these transitions for people, agents, and automations."}
              </p>
              <Button disabled={state.saving} onClick={() => void save()}>
                {state.saving ? "Saving…" : "Save workflow"}
              </Button>
            </SheetFooter>
          ) : null}
        </SheetPopup>
      </Sheet>
    </>
  );
}
